import Immutable from 'immutable';
import moment from 'moment';

const LOAD = 'treatingDoctor/LOAD';
const LOAD_SUCCESS = 'treatingDoctor/LOAD_SUCCESS';
const LOAD_FAIL = 'treatingDoctor/LOAD_FAIL';

const REFRESH_SIGNERS         = 'treatingDoctor/REFRESH_SIGNERS';
const REFRESH_SIGNERS_SUCCESS = 'treatingDoctor/REFRESH_SIGNERS_SUCCESS';
const REFRESH_SIGNERS_FAIL    = 'treatingDoctor/REFRESH_SIGNERS_FAIL';

const LOAD_SIGNERS         = 'treatingDoctor/LOAD_SIGNERS';
const LOAD_SIGNERS_SUCCESS = 'treatingDoctor/LOAD_SIGNERS_SUCCESS';
const LOAD_SIGNERS_FAIL    = 'treatingDoctor/LOAD_SIGNERS_FAIL';

const LOAD_PRACTICES_STATUS         = 'treatingDoctor/LOAD_PRACTICES_STATUS';
const LOAD_PRACTICES_STATUS_SUCCESS = 'treatingDoctor/LOAD_PRACTICES_STATUS_SUCCESS';
const LOAD_PRACTICES_STATUS_FAIL    = 'treatingDoctor/LOAD_PRACTICES_STATUS_FAIL';

const REFRESH_PRACTICE         = 'treatingDoctor/REFRESH_PRACTICE';
const REFRESH_PRACTICE_SUCCESS = 'treatingDoctor/REFRESH_PRACTICE_SUCCESS';
const REFRESH_PRACTICE_FAIL    = 'treatingDoctor/REFRESH_PRACTICE_FAIL';

const initialState = Immutable.fromJS({
    isLoad: false,
    reportData: null,
    savedAt: null,
    contractTypeByAbbr: {},
    hygPayoutPractices: [],
    loadErr: null,
    isRefreshingSigners: false,
    refreshSignersErr: null,
    isLoadingSigners: false,
    noteSigners: null,
    noteSignersNotFound: false,
    noteSignersS3Error: null,
    noteSignersErr: null,
    practicesStatus: null,
    isLoadingPracticesStatus: false,
    practicesStatusErr: null,
    // Per-practice refresh state: { [dbName]: 'pending'|'running'|'done'|'error' }
    practiceRefreshStatus: {},
});

export default function reducer(state = initialState, action) {
    switch (action.type) {
        case LOAD:
            return state
                .set('isLoad', true)
                .set('loadErr', null);

        case LOAD_SUCCESS:
            return state
                .set('isLoad', false)
                .set('reportData', action.res?.treatments ?? action.res)
                .set('savedAt', action.res?.savedAt ?? null)
                .set('contractTypeByAbbr', Immutable.fromJS(action.res?.contractTypeByAbbr ?? {}))
                .set('hygPayoutPractices', Immutable.fromJS(action.res?.hygPayoutPractices ?? []))
                .set('loadErr', null);

        case LOAD_FAIL:
            return state
                .set('isLoad', false)
                .set('loadErr', action.error)
                .set('reportData', null);

        case REFRESH_SIGNERS:
            return state
                .set('isRefreshingSigners', true)
                .set('refreshSignersErr', null);

        case REFRESH_SIGNERS_SUCCESS:
            return state
                .set('isRefreshingSigners', false)
                .set('noteSigners', action.data ?? state.get('noteSigners'))
                .set('noteSignersNotFound', false)
                .set('noteSignersS3Error', null);

        case REFRESH_SIGNERS_FAIL:
            return state
                .set('isRefreshingSigners', false)
                .set('refreshSignersErr', action.error);

        case LOAD_SIGNERS:
            return state
                .set('isLoadingSigners', true)
                .set('noteSigners', null)
                .set('noteSignersNotFound', false)
                .set('noteSignersS3Error', null)
                .set('noteSignersErr', null);

        case LOAD_SIGNERS_SUCCESS:
            return state
                .set('isLoadingSigners', false)
                .set('noteSigners', action.data)
                .set('noteSignersNotFound', action.notFound || false)
                .set('noteSignersS3Error', action.s3Error || null);

        case LOAD_SIGNERS_FAIL:
            return state
                .set('isLoadingSigners', false)
                .set('noteSignersErr', action.error);

        case LOAD_PRACTICES_STATUS:
            return state
                .set('isLoadingPracticesStatus', true)
                .set('practicesStatusErr', null);

        case LOAD_PRACTICES_STATUS_SUCCESS:
            return state
                .set('isLoadingPracticesStatus', false)
                .set('practicesStatus', action.data);

        case LOAD_PRACTICES_STATUS_FAIL:
            return state
                .set('isLoadingPracticesStatus', false)
                .set('practicesStatusErr', action.error);

        case REFRESH_PRACTICE: {
            const prev = (state.get('practiceRefreshStatus') || Immutable.Map()).toJS();
            return state.set('practiceRefreshStatus', Immutable.fromJS({ ...prev, [action.practice]: 'running' }));
        }

        case REFRESH_PRACTICE_SUCCESS: {
            const prev = (state.get('practiceRefreshStatus') || Immutable.Map()).toJS();
            // Also update updatedAt in practicesStatus list
            const practicesStatus = state.get('practicesStatus');
            let updatedPracticesStatus = practicesStatus;
            if (practicesStatus) {
                const ps = practicesStatus.toJS ? practicesStatus.toJS() : practicesStatus;
                updatedPracticesStatus = Immutable.fromJS({
                    ...ps,
                    practices: (ps.practices || []).map(p =>
                        p.dbName === action.practice
                            ? { ...p, updatedAt: action.updatedAt }
                            : p
                    ),
                });
            }
            return state
                .set('practiceRefreshStatus', Immutable.fromJS({ ...prev, [action.practice]: 'done' }))
                .set('practicesStatus', updatedPracticesStatus);
        }

        case REFRESH_PRACTICE_FAIL: {
            const prev = (state.get('practiceRefreshStatus') || Immutable.Map()).toJS();
            return state.set('practiceRefreshStatus', Immutable.fromJS({ ...prev, [action.practice]: 'error' }));
        }

        default:
            return state;
    }
}

export const getTreatingDoctorReport = (params = '') => async (dispatch, getState, api) => {
    dispatch({ type: LOAD });
    try {
        const data = await api.get(`payReports/treating-doctor/${params}`);
        dispatch({ type: LOAD_SUCCESS, res: data });
    } catch (err) {
        dispatch({ type: LOAD_FAIL, error: err.message });
    }
};

export const saveTreatingDoctorOverride = (payload) => async (dispatch, getState, api) => {
    try {
        const data = await api.post('payReports/treating-doctor/save-override', { data: payload });
        return data; // Return backend response
    } catch (err) {
        throw err;
    }
};

export const saveTreatingDoctorBulkOverride = (payload) => async (dispatch, getState, api) => {
    try {
        const data = await api.post('payReports/treating-doctor/save-bulk-override', { data: payload });
        return data; // Return backend response
    } catch (err) {
        throw err;
    }
};

export const getTreatingDoctorOverrides = (startDate, endDate) => async (dispatch, getState, api) => {
    let start = startDate;
    let end = endDate;

    if (!start || !end) {
        start = moment().startOf('month').format('YYYY-MM-DD');
        end = moment().endOf('month').format('YYYY-MM-DD');
    }

    try {
        const data = await api.get(`payReports/treating-doctor/get-overrides?startDate=${start}&endDate=${end}`);
        return data;
    } catch (err) {
        // console.error("Failed to fetch overrides", err);
        return []; // Return empty array on failure to avoid UI breaks
    }
};

export const deleteTreatingDoctorOverride = (id) => async (dispatch, getState, api) => {
    try {
        const data = await api.del(`payReports/treating-doctor/delete-override/${id}`);
        return data; // Return backend response
    } catch (err) {
        throw err;
    }
};

export const getNoteSigners = (year, month) => async (dispatch, getState, api) => {
    dispatch({ type: LOAD_SIGNERS });
    try {
        const params = year && month ? { year, month } : {};
        const data = await api.get('payReports/treating-doctor/notes/signers', { params });
        console.log('[getNoteSigners] raw response:', data);
        if (data && data.notFound) {
            dispatch({ type: LOAD_SIGNERS_SUCCESS, data: null, notFound: true, s3Error: data.s3Error || null });
        } else {
            const signers = data?.data ?? data;
            const practiceKeys = signers ? Object.keys(signers) : [];
            const totalRows = signers ? practiceKeys.reduce((sum, k) => sum + (signers[k]?.length || 0), 0) : 0;
            console.log(`[getNoteSigners] ${practiceKeys.length} practices, ${totalRows} total rows`);
            console.log('[getNoteSigners] rows per practice:', Object.fromEntries(practiceKeys.map(k => [k, signers[k]?.length])));
            dispatch({ type: LOAD_SIGNERS_SUCCESS, data: signers, notFound: false, s3Error: null });
        }
        return data;
    } catch (err) {
        dispatch({ type: LOAD_SIGNERS_FAIL, error: err.message });
        return null;
    }
};

/**
 * Force a fresh DB pull and overwrite the S3 cache for the given year/month.
 * @param {string} year  e.g. '2026'
 * @param {string} month e.g. '03'
 */
export const refreshNoteSigners = (year, month) => async (dispatch, getState, api) => {
    dispatch({ type: REFRESH_SIGNERS });
    try {
        const params = { refresh: true, ...(year && month ? { year, month } : {}) };
        const data = await api.get('payReports/treating-doctor/notes/signers', { params });
        if (data && data.s3SaveError) {
            dispatch({ type: REFRESH_SIGNERS_FAIL, error: data.s3SaveError });
            throw new Error(data.s3SaveError);
        }
        dispatch({ type: REFRESH_SIGNERS_SUCCESS, data: data?.data ?? data });
        return data;
    } catch (err) {
        dispatch({ type: REFRESH_SIGNERS_FAIL, error: err.message });
        throw err;
    }
};

/**
 * Load the list of all practices with their last-updated timestamps from S3.
 * @param {string} year  e.g. '2026'
 * @param {string} month e.g. '03'
 */
export const getPracticesStatus = (year, month) => async (dispatch, _getState, api) => {
    dispatch({ type: LOAD_PRACTICES_STATUS });
    try {
        const params = {};
        if (year && month) { params.year = year; params.month = month; }
        const data = await api.get('payReports/treating-doctor/notes/signers/practices-status', { params });
        dispatch({ type: LOAD_PRACTICES_STATUS_SUCCESS, data });
        return data;
    } catch (err) {
        dispatch({ type: LOAD_PRACTICES_STATUS_FAIL, error: err.message });
        throw err;
    }
};

/**
 * Refresh note signers for a single practice for the given month.
 * @param {string} practice  Practice DB name
 * @param {string} year      e.g. '2026'
 * @param {string} month     e.g. '03'
 */
export const refreshPracticeSigners = (practice, year, month) => async (dispatch, _getState, api) => {
    dispatch({ type: REFRESH_PRACTICE, practice });
    try {
        const data = await api.post('payReports/treating-doctor/notes/signers/refresh-practice', {
            data: { practice, year, month },
        });
        dispatch({ type: REFRESH_PRACTICE_SUCCESS, practice, updatedAt: data.updatedAt });
        return data;
    } catch (err) {
        dispatch({ type: REFRESH_PRACTICE_FAIL, practice, error: err.message });
        throw err;
    }
};
